"use client";

import { useState, useMemo, useCallback, useRef } from "react";
import { Card, CardBody, Skeleton, Chip, Button, useDisclosure, Pagination, Image, CardFooter, ScrollShadow, cn } from "@heroui/react";
import { type NFTToken } from "@/hooks/portfolio/useNFTCollectionOptimized";
import { useNFTCollection } from "@/hooks/portfolio/useNFTCollection";
import { useSelectedNFTs } from "@/hooks/portfolio/useSelectedNFTs";
import { useNFTViewer } from "@/hooks/useNFTViewer";
import { type Address } from "viem";
import { type ChainType } from "@/constants/chain-specific";
import { Icon as IconifyIcon } from '@iconify/react';
import { getCollectionByAddress, isCollectionWhitelisted } from "@/constants/marketplace/collections";
import { useDirectListings } from "@/hooks/marketplace/useDirectListings";
import { SellNFTDialog } from '@/components/marketplace';
import toast from '@/util/toast';
import React from "react";
import { useOptimizedTokenLoading } from '@/hooks/useOptimizedTokenLoading';
import { processIPFSUrl, getOptimizedImageUrl } from '@/util/ipfs';
import { useOptimistic, useTransition } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { cancelListingOnNFT } from '@/hooks/marketplace/useCancelListing';
import { findTokenByAddress } from "./PortfolioDrawer";
const ITEMS_PER_PAGE = 10; // Reduced for better drawer UX - shows pagination sooner

const fmt = new Intl.NumberFormat('en', {
  notation: 'compact',
  compactDisplay: 'short',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
});
interface NFTCollectionGridPaginatedProps {
  contractAddress: Address;
  collectionName: string;
  chainId: ChainType;
  userAddress: Address;
  knownBalance?: number; // Pre-verified balance from verification hook
}
interface NFTCardProps {
  token: NFTToken;
  isSelected: boolean;
  onToggleSelect: () => void;
  onViewNFT: () => void;
  chainId: ChainType;
  isListed?: boolean;
  isWhitelisted?: boolean;
  onSellRequest: (token: NFTToken) => void;
  tokenListing?: any; // The actual listing data for cancel functionality
  onCancelListing?: (listingId: string) => void;
}
interface SelectedNFT {
  id: string;
  contractAddress: Address;
  tokenId: string;
  collectionName: string;
  image?: string;
  name?: string;
}

// Lightweight NFT Card for pagination
const NFTCard = React.memo<NFTCardProps>(({
  token,
  isSelected,
  onToggleSelect,
  onViewNFT,
  chainId,
  isListed,
  isWhitelisted,
  onSellRequest,
  tokenListing,
  onCancelListing
}) => {
  const [processing, setProcessing] = useState(false);
  const [isPending, startTransition] = useTransition();

  // Use optimized IPFS image URL
  const optimizedImageUrl = useMemo(() => {
    if (!token.metadata?.image) {
      return null;
    }
    // For NFT grid cards, use 300x300 with good quality for crisp display
    return getOptimizedImageUrl(token.metadata.image.trim(), 200, 200, 88, 'webp', 'contain');
  }, [token.metadata?.image]);
  const handleCardClick = useCallback(() => {
    startTransition(() => {
      onToggleSelect();
    });
  }, [onToggleSelect]);
  const handleViewPress = useCallback(() => {
    startTransition(() => {
      onViewNFT();
    });
  }, [onViewNFT]);
  const handleSellPress = useCallback(() => {
    startTransition(() => {
      onSellRequest(token);
    });
  }, [onSellRequest, token]);
  const handleCancelListingPress = useCallback(async () => {
    if (!tokenListing || !onCancelListing) return;
    try {
      setProcessing(true);
      await onCancelListing(tokenListing.listingId);
      toast.success('Listing cancelled successfully!');
    } catch (error) {
      console.error('Error cancelling listing:', error);
      toast.error('Failed to cancel listing');
    } finally {
      setProcessing(false);
    }
  }, [tokenListing, onCancelListing]);

  // Determine if this is a trading card collection
  const collection = getCollectionByAddress(token.contractAddress, chainId);
  const isTradingCard = collection?.isTradingCard;
  const listingToken = findTokenByAddress(tokenListing?.currency, chainId);
  return <Card className={cn("w-full", isSelected && "shadow-white shadow-inner")} onPress={handleCardClick} isPressable>
      <CardBody className="p-0 flex flex-col">
        <div className={`relative w-full overflow-hidden rounded-t-lg ${isTradingCard ? 'aspect-[3/4]' : 'aspect-square'}`}>
          {/* HeroUI Image with built-in loading skeleton and fallback handling */}
          <Image src={optimizedImageUrl || undefined} alt={token.metadata?.name || `Token #${token.tokenId.toString()}`} className="w-full h-full object-cover" loading="lazy" classNames={{
          wrapper: "w-full h-full place-self-center",
          img: "w-full h-full object-cover"
        }} />

          {/* Status badges */}
          <div className="absolute top-2 right-2 flex flex-col gap-1">
            {isListed && <Chip size="sm" color="success" variant="flat">
                Listed
              </Chip>}
            {token.metadata?._lazyLoaded && <Chip size="sm" color="primary" variant="flat">
                Fast
              </Chip>}
          </div>


        </div>

        <div className="p-2">
          <div className="text-sm font-medium truncate mb-1">
            {token.metadata?.name || `Token #${token.tokenId.toString()}`}
          </div>

          <div className="text-xs text-default-500 mb-2">
            #{token.tokenId.toString()}
          </div>
        </div>
      </CardBody>

      <CardFooter>
        <div className="mt-auto w-full">
          {(() => {
          if (isListed && tokenListing && onCancelListing) {
            return <Button size="sm" variant="flat" fullWidth color="danger" className="w-full text-xs h-7" startContent={<IconifyIcon icon="mdi:cancel" className="text-sm" />} onPress={handleCancelListingPress} isDisabled={processing}>
                  {processing ? 'Cancelling...' : `Listed for ${fmt.format(Number(tokenListing.pricePerToken) / 10 ** (listingToken?.decimals ?? 18))} ${listingToken?.symbol}`}
                </Button>;
          }
          if (isSelected && isWhitelisted) {
            return <Button size="sm" fullWidth color="secondary" className="w-full text-xs" startContent={<IconifyIcon icon="mdi:tag" className="text-sm" />} onPress={handleSellPress}>
                  Sell
                </Button>;
          }
          return <Button size="sm" fullWidth variant="flat" color="primary" className="w-full text-xs h-7" startContent={<IconifyIcon icon="mdi:eye" className="text-sm" />} onPress={handleViewPress}>
                View
              </Button>;
        })()}
        </div>
      </CardFooter>
    </Card>;
});
NFTCard.displayName = 'NFTCard';

// Main paginated grid component
export function NFTCollectionGridPaginated({
  contractAddress,
  collectionName,
  chainId,
  userAddress,
  knownBalance
}: NFTCollectionGridPaginatedProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isPending, startTransition] = useTransition();
  const parentRef = useRef<HTMLDivElement>(null);
  const [processing, setProcessing] = useState(false);

  // Get collection data with optimized hook
  const {
    tokens,
    balance: totalBalance,
    isLoading,
    currentBalance,
    refetch: refresh
  } = useNFTCollection(contractAddress, collectionName, chainId, userAddress, knownBalance);

  // Debug logging for NFT collection data
  React.useEffect(() => {
    console.log('NFTCollectionGridPaginated Debug:', {
      contractAddress,
      collectionName,
      chainId,
      userAddress,
      tokens: tokens?.length || 0,
      totalBalance,
      isLoading,
      currentBalance
    });
  }, [contractAddress, collectionName, chainId, userAddress, tokens, totalBalance, isLoading, currentBalance]);

  // Get collection info separately
  const collection = useMemo(() => {
    return getCollectionByAddress(contractAddress, chainId);
  }, [contractAddress, chainId]);

  // Selection management
  const {
    selectedNFTs,
    selectedCount,
    toggleNFT,
    isSelected,
    clearSelection
  } = useSelectedNFTs();

  // Use optimistic updates for selection
  const [optimisticSelectedNFTs, setOptimisticSelectedNFTs] = useOptimistic<SelectedNFT[]>(selectedNFTs);

  // Use optimized token loading
  const {
    currentPageTokens,
    isLoadingMetadata,
    progress
  } = useOptimizedTokenLoading({
    tokens,
    currentPage,
    itemsPerPage: ITEMS_PER_PAGE,
    contractAddress,
    chainId,
    isERC1155: collection?.type === 'ERC1155'
  });

  // Get marketplace listings
  const {
    listings,
    refreshListings
  } = useDirectListings(chainId);

  // NFT viewer
  const {
    openNFTModal
  } = useNFTViewer();

  // Sell dialog
  const {
    isOpen: isSellOpen,
    onOpen: onSellOpen,
    onClose: onSellClose
  } = useDisclosure();
  const [selectedNFTForSell, setSelectedNFTForSell] = useState<NFTToken | null>(null);

  // Cancel listing handler
  const handleCancelListing = useCallback(async (listingId: string) => {
    try {
      setProcessing(true);
      await cancelListingOnNFT({
        listingId: BigInt(listingId),
        chainId
      });
      toast.success('Listing cancelled successfully!');
      refreshListings();
    } catch (error) {
      console.error('Error cancelling listing:', error);
      toast.error('Failed to cancel listing');
    } finally {
      setProcessing(false);
    }
  }, []);

  // Check if collection is whitelisted
  const isWhitelisted = useMemo(() => {
    return isCollectionWhitelisted(contractAddress, chainId);
  }, [contractAddress, chainId]);

  // Pagination calculations - use totalBalance for proper pagination
  const totalPages = Math.ceil(totalBalance / ITEMS_PER_PAGE);

  // Handle page change with transition
  const handlePageChange = useCallback((page: number) => {
    startTransition(() => {
      setCurrentPage(page);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }, []);

  // Loading state - only show full loading if no tokens at all
  if (isLoading && tokens.length === 0) {
    return <motion.div initial={{
      opacity: 0,
      x: 0
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: 0
    }} transition={{
      type: "spring",
      stiffness: 300,
      damping: 30,
      duration: 0.5
    }} className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-default-50 rounded-lg">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-8 w-24" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4">
          {Array.from({
          length: ITEMS_PER_PAGE
        }).map((_, index) => <Card key={index} className="w-full h-full">
              <CardBody className="p-0 h-full flex flex-col">
                <Skeleton className="aspect-square w-full" />
                <div className="p-2 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-7 w-full" />
                </div>
              </CardBody>
            </Card>)}
        </div>
      </motion.div>;
  }

  // Empty state
  if (!isLoading && tokens.length === 0) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      type: "spring",
      damping: 30,
      duration: 0.5
    }} className="flex flex-col items-center justify-center p-8 text-center">
        <IconifyIcon icon="mdi:image-off" className="text-6xl text-default-400 mb-4" />
        <div className="text-lg font-medium text-default-600">No NFTs found</div>
        <div className="text-sm text-default-400">
          This collection doesn't contain any tokens for this address
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    type: "spring",
    damping: 30,
    duration: 0.5
  }} className="">
      <div className="flex flex-col h-[calc(100vh-110px)] overflow-x-hidden">
        {/* Scrollable Grid */}
        <div className="flex-1 min-h-0">
          <ScrollShadow className="h-full" size={10}>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-4 overflow-x-hidden">
              {currentPageTokens.map(token => {
              const tokenKey = `${token.contractAddress}-${token.tokenId.toString()}`;
              const isTokenSelected = isSelected(tokenKey);

              // Debug logging for cross-collection contamination
              if (token.contractAddress.toLowerCase() !== contractAddress.toLowerCase()) {
                console.error(`🚨 CROSS-COLLECTION CONTAMINATION DETECTED:`, {
                  expectedContract: contractAddress,
                  actualContract: token.contractAddress,
                  tokenId: token.tokenId.toString(),
                  collectionName,
                  metadata: token.metadata
                });
              }

              // Find the specific listing for this token
              const tokenListing = listings?.find(listing => listing.assetContract.toLowerCase() === contractAddress.toLowerCase() && listing.tokenId.toString() === token.tokenId.toString() && listing.status === 1 // CREATED status
              );
              const isTokenListed = !!tokenListing;
              return <AnimatePresence mode="wait">
                    <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  duration: 0.25
                }} layout key={tokenKey} className="w-full max-w-[220px] overflow-x-hidden" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                      <NFTCard token={token} isSelected={isTokenSelected} onToggleSelect={() => {
                    const nftData = {
                      id: tokenKey,
                      contractAddress: token.contractAddress,
                      tokenId: token.tokenId.toString(),
                      collectionName: token.collectionName,
                      image: token.metadata?.image,
                      name: token.metadata?.name
                    };
                    toggleNFT(nftData);
                  }} onViewNFT={() => {
                    const isERC1155 = collection?.type === 'ERC1155';
                    openNFTModal({
                      chainId,
                      contractAddress: token.contractAddress,
                      tokenId: token.tokenId,
                      initialData: token.metadata ? {
                        metadata: token.metadata,
                        owner: null,
                        isERC1155,
                        balance: undefined
                      } : undefined
                    });
                  }} chainId={chainId} isListed={isTokenListed} isWhitelisted={isWhitelisted} onSellRequest={token_0 => {
                    setSelectedNFTForSell(token_0);
                    onSellOpen();
                  }} tokenListing={tokenListing} onCancelListing={handleCancelListing} />
                    </motion.div>
                  </AnimatePresence>;
            })}
            </div>

          </ScrollShadow>
        </div>

        {/* Fixed Pagination */}

        {totalPages > 1 && <div className="flex-none bg-background/80 backdrop-blur-sm py-2 overflow-x-hidden">
            <div className="flex flex-col items-center gap-2">
              <div className="text-xs text-default-500">
                Page {currentPage} of {totalPages} • Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalBalance)} of {totalBalance} NFTs
              </div>
              <Pagination total={totalPages} page={currentPage} onChange={handlePageChange} showControls size="sm" classNames={{
            wrapper: "gap-1 overflow-visible pb-2",
            item: "w-8 h-8 text-small rounded-md bg-default-100/50 hover:bg-default-200",
            cursor: "bg-primary text-primary-foreground font-bold shadow-md"
          }} />
            </div>
          </div>}


        {/* Show total count even for single page */}


        {totalPages === 1 && totalBalance > 4 && <div className="flex-none bg-background/80 backdrop-blur-sm border-t border-default-200 py-2 overflow-x-hidden">
            <div className="flex justify-center">
              <div className="text-xs text-default-500 bg-default-50 px-3 py-2 rounded-full">
                Showing all {totalBalance} NFTs
              </div>
            </div>
          </div>}


        {/* Sell NFT Dialog */}


        {selectedNFTForSell && <SellNFTDialog chainId={chainId} contractAddress={contractAddress} tokenId={selectedNFTForSell.tokenId.toString()} collectionName={collection?.name || collectionName} isOpen={isSellOpen} onClose={() => {
        onSellClose();
        setSelectedNFTForSell(null);
        clearSelection();
      }} onSuccess={() => {
        clearSelection();
        refresh();
      }} />}

      </div>
    </motion.div>;
}